import type { EdlinkIntegration, EdlinkSyncPreviewData } from '@magicschool/business-logic/edlink-sync';
import { type ExtendedOrg, type ExtendedOrgRequest, isValidCountryCode } from '@magicschool/business-logic/o';
import { toast } from '@magicschool/spellbook';
import { DEFAULT_LOCALE, type Enums, type Tables } from '@magicschool/supabase/types';
import { subscriptionPlans } from '@magicschool/supabase/user/utils';
import type { OrgsSearchResponse } from 'app/api/admin/orgs/search/route';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import { getEdlinkIntegration } from '@/app/_actions/orgs/syncs/getEdlinkIntegration';
import { getSyncPreview } from '@/app/_actions/orgs/syncs/getSyncPreview';
import { listEdlinkIntegrations } from '@/app/_actions/orgs/syncs/listEdlinkIntegrations';
import { saveSyncPreview } from '@/app/_actions/orgs/syncs/saveSyncPreview';
import { syncOrg } from '@/app/_actions/orgs/syncs/syncOrg';
import { type UpdateSyncPreviewStatus, updateSyncPreview } from '@/app/_actions/orgs/syncs/updateSyncPreview';
import { AuthorizationError } from '@/features/server/actions/authErrors';

export const orgTypes: Enums<'org_type'>[] = ['School', 'District', 'Group', 'Organization', 'MagicSchool'] as const;
export type Domain = {
  idx: number;
  value: string;
  set_user_org_id: boolean;
};

export type AdminOrgsStore = {
  status?: number;
  loading: boolean;
  loadingOrgEmails: boolean;
  syncing: boolean;
  fetchingIntegration: boolean;
  fetchingPreviewSync: boolean;
  lastSynced: string | null;
  upserting: boolean;
  orgs: OrgsSearchResponse['orgs'];
  currentOrg: ExtendedOrg;
  savedOrg: ExtendedOrg;
  currentOrgEmailCount: number;
  currentDomain: Domain;
  syncPreview: EdlinkSyncPreviewData | null;
  domainModalOpen: boolean;
  initialize: () => void;
  upsert: () => Promise<ExtendedOrg | void>;
  load: (orgId: string) => Promise<void>;
  setField: SetField<AdminOrgsStore>;
  validate: () => void;
  getIntegration: (integrationId: string | null) => Promise<EdlinkIntegration | undefined>;
  listIntegrations: () => Promise<EdlinkIntegration[] | undefined>;
  getSyncPreview: () => Promise<EdlinkSyncPreviewData | null>;
  saveSyncPreview: (values: EdlinkSyncPreviewData) => void;
  updateSyncPreview: (previewId: string, status: UpdateSyncPreviewStatus) => void;
  doSync: () => Promise<void>;
  searchQuery: string;
  formErrors: Record<string, string> | null;
};

const emptyOrg = (): Tables<'orgs'> => ({
  id: '',
  name: '',
  type: orgTypes[0],
  logo_url: '',
  created_at: '',
  updated_at: '',
  auth_providers: [],
  edlink_access_token_id: '',
  edlink_integration_id: '',
  active: true,
  is_demo: false,
  locale: DEFAULT_LOCALE,
  salesforce_id: null,
  country_code: null,
  updated_by_user_id: null,
  deleted_at: null,
  last_admin_update_dt: null,
  plan_tier: subscriptionPlans.PARTNER,
});

export const emptyExtendedOrg = () => ({
  ...emptyOrg(),
  domains: [],
  description: null,
  emailCount: 0,
  orgEmails: [],
  orgAdminEmails: [],
  enableRequireLogin: false,
  enableEdlinkSync: false,
  enforceStudentSSO: false,
  requireLoginDefault: false,
  enableSyncDeletionGuard: true,
  integrationsPipelineOnly: false,
  syncStatus: null,
  lastSyncPreview: null,
  lastSyncDate: null,
  syncApproved: null,
  scheduledActions: null,
  scheduledPauseTask: false,
  scheduledActivateTask: false,
  scheduledDeleteTask: false,
  hasScheduledActions: false,
  lastUpdatedBy: null,
  status: null,
});

const emptyDomain = (): Domain => ({
  idx: 0,
  value: '',
  set_user_org_id: false,
});

const defaultState = {
  loading: true,
  loadingOrgEmails: true,
  syncing: false,
  fetchingIntegration: false,
  fetchingPreviewSync: false,
  lastSynced: '',
  upserting: false,
  orgs: [],
  currentOrg: emptyExtendedOrg(),
  savedOrg: emptyExtendedOrg(),
  currentOrgEmailCount: 0,
  currentDomain: emptyDomain(),
  domainModalOpen: false,
  searchQuery: '',
  formErrors: null,
  syncPreview: null,
};

export const createAdminOrgsStoreSlice = createStoreSlice('AdminOrgsStoreData', defaultState, ({ get, set, setField }) => ({
  setField,
  initialize: () => set({ ...defaultState, loading: false }),
  load: async (orgId: string) => {
    set({ loading: true, formErrors: null, syncing: false });
    const response = await fetch<ExtendedOrg>(`/api/admin/orgs/${orgId}`);
    const data = await response.json();
    set({
      status: response.status,
      currentOrg: data,
      savedOrg: structuredClone(data),
      syncing: data.syncStatus === 'syncing',
      loading: false,
      lastSynced: data.lastSyncDate,
    });
  },
  upsert: async () => {
    get().validate();
    const { currentOrg: org, formErrors } = get();
    org.name = org.name.trim();
    org.salesforce_id = org.salesforce_id?.trim() || null;
    org.edlink_integration_id = org.edlink_integration_id?.trim() || null;

    if (!formErrors) {
      set({ upserting: true });
      let url = `/api/admin/orgs`;

      if (org.id) url = url.concat(`/${org.id}`);
      const response = await fetch<ExtendedOrg>(url, {
        method: org.id ? 'PUT' : 'POST',
        body: JSON.stringify({ org } satisfies ExtendedOrgRequest),
        onSuccess: () => {
          toast({ title: `Org successfully ${org.id ? 'updated' : 'created'}`, status: 'success' });
          set({ upserting: false, savedOrg: structuredClone(get().currentOrg) });
        },
        responseErrorHandlers: {
          conflict: async ({ response }) => {
            const res = await response.json<{ error: string }>();
            const formErrors: Record<string, string> = {};
            if (res.error.indexOf('org_domain_key') >= 0) {
              formErrors.domain = 'Domain already exists.';
            } else {
              formErrors.name = 'Org name already exists.';
            }
            set({ upserting: false, formErrors: formErrors });
          },
          // shortCircuit: false so monkey-fetch still runs the global forbidden toast; we only clear upserting so the form doesn't stay disabled.
          forbidden: () => {
            set({ upserting: false });
            return { shortCircuit: false };
          },
        },
      });
      // Non-ok JSON is still truthy — callers treat defined as success, so return undefined here.
      if (!response.ok) return;
      try {
        return await response.json();
      } catch (_error) {
        return;
      }
    }
  },
  validate: () => {
    const { currentOrg } = get();
    const formErrors: Record<string, string> = {};
    if (!currentOrg.name?.trim()) {
      formErrors.name = 'Name is required';
    }
    if (!currentOrg.orgAdminEmails?.length && !currentOrg.is_demo && !currentOrg.id) {
      formErrors.orgAdminEmails = 'At least one admin email is required for non demo orgs';
    }
    if (currentOrg.salesforce_id && !/^[a-zA-Z0-9]{15,18}$/.test(currentOrg.salesforce_id)) {
      formErrors.salesforce_id = 'Salesforce Id must be 15-18 alphanumeric characters';
    }
    if (currentOrg.country_code && !isValidCountryCode(currentOrg.country_code)) {
      formErrors.country_code = 'Country must be a valid ISO 3166-1 alpha-2 code';
    }
    set({ formErrors: Object.keys(formErrors).length ? formErrors : null });
  },
  getIntegration: async (integrationId) => {
    set({ fetchingIntegration: true });
    const { data } = await getEdlinkIntegration(integrationId);
    const integration = data?.$data;
    set({ fetchingIntegration: false });
    return integration;
  },
  listIntegrations: async () => {
    set({ fetchingIntegration: true });
    const { data } = await listEdlinkIntegrations();
    const integrations = data?.$data;
    set({ fetchingIntegration: false });
    return integrations;
  },
  getSyncPreview: async () => {
    set({ fetchingPreviewSync: true });
    const { currentOrg } = get();
    const { data } = await getSyncPreview(currentOrg);
    const isAuthError = data instanceof AuthorizationError;

    if (data && !isAuthError) {
      const syncPreview = data;
      set({ fetchingPreviewSync: false, syncPreview });
      return syncPreview;
    }

    return null;
  },
  saveSyncPreview: async (values: EdlinkSyncPreviewData) => {
    const { currentOrg, syncPreview } = get();
    const result = await saveSyncPreview(currentOrg, values);
    const id = result?.data?.data?.id;

    if (id && syncPreview !== null) {
      set({ syncPreview: Object.assign(syncPreview, { id }) });
    }
  },
  updateSyncPreview: async (syncPreviewId: string, status: UpdateSyncPreviewStatus) => {
    const { currentOrg } = get();
    const orgId = currentOrg.id;
    await updateSyncPreview(orgId, syncPreviewId, status);
  },
  doSync: async () => {
    set({ syncing: true });
    const { currentOrg } = get();
    const triggerId = await syncOrg(currentOrg.id);
    if (triggerId && !(triggerId instanceof AuthorizationError)) {
      toast({ title: `Sync triggered successfully (${triggerId.data}). Please check back soon.`, status: 'success' });
    } else {
      toast({ title: `Failed to trigger sync. ${triggerId.message}`, status: 'error' });
      set({ syncing: false });
    }
  },
}));
